package floris.graphics.backoffice.pages.admin.categories.manage

import floris.graphics.models.domain.models.Category
import floris.graphics.models.dto.requests.category.InsertCategory
import floris.graphics.models.dto.requests.category.UpdateCategory
import floris.graphics.repo.CategoryRepo
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers.Default
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.channels.Channel
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.receiveAsFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

class ManageCategoryViewModel(
    private val categoryId: String? = null,
    private val categoryRepository: CategoryRepo = CategoryRepo,
    private val coroutineScope: CoroutineScope = CoroutineScope(SupervisorJob() + Default)
) {
    private val _state = MutableStateFlow(ManageCategoryState())
    val state = _state.asStateFlow()

    private val _eventFlow = Channel<ManageCategoryUiEvent>()
    val eventFlow = _eventFlow.receiveAsFlow()

    fun onEvent(event: ManageCategoryEvent) = coroutineScope.launch {
        when (event) {
            ManageCategoryEvent.LoadManageCategory -> getCategory()
            ManageCategoryEvent.Save -> saveCategory()
            ManageCategoryEvent.Cancel -> _eventFlow.send(ManageCategoryUiEvent.NavigateBack)
            ManageCategoryEvent.Delete -> deleteCategory()
            is ManageCategoryEvent.UpdateName -> {
                val name = event.name
                _state.update { it.copy(name = name) }
            }
        }
    }

    init {
        onEvent(ManageCategoryEvent.LoadManageCategory)
    }

    private suspend fun getCategory() {
        if (categoryId.isNullOrBlank()) return

        val category = categoryRepository.getCategory(categoryId) ?: Category()
        _state.value = ManageCategoryState(originalCategory = category, name = category.name)
    }

    private suspend fun saveCategory() {
        val state = _state.value

        val result = if (state.originalCategory.id.isBlank()) {
            val category = InsertCategory(
                name = state.name.trim(),
                parentCategoryId = state.parentCategory
            )
            categoryRepository.insertCategory(category)
        } else {
            val category = UpdateCategory(
                name = state.name.trim(),
                parentCategoryId = state.parentCategory
            )
            categoryRepository.updateCategory(state.originalCategory.id, category)
        }

        if (result != null) {
            _eventFlow.send(ManageCategoryUiEvent.NavigateBack)
        } else {
            _eventFlow.send(ManageCategoryUiEvent.ShowError)
        }
    }

    private suspend fun deleteCategory() {
        if (categoryId.isNullOrBlank()) return

        categoryRepository.deleteCategory(categoryId)
        _eventFlow.send(ManageCategoryUiEvent.NavigateBack)
    }
}

data class ManageCategoryState(
    val originalCategory: Category = Category(),
    val name: String = "",
    val parentCategory: String? = null,
    val errorText: String = ""
) {
    private val nameChanged = name != originalCategory.name

    val isCreationFlow: Boolean
        get() = originalCategory.id.isBlank()

    val hasChanged: Boolean
        get() = nameChanged
}

sealed class ManageCategoryEvent {
    data object LoadManageCategory : ManageCategoryEvent()
    data class UpdateName(val name: String) : ManageCategoryEvent()
    data object Save : ManageCategoryEvent()
    data object Cancel : ManageCategoryEvent()
    data object Delete : ManageCategoryEvent()
}

sealed class ManageCategoryUiEvent {
    data object ShowError : ManageCategoryUiEvent()
    data object NavigateBack : ManageCategoryUiEvent()
}
